import { useState, useEffect, useCallback } from 'react';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import EquipmentGroupsSection from './EquipmentGroupsSection';

interface TechnicalSpec {
  inverterType?: string;
  inverterCount?: number;
  panelType?: string;
  panelCount?: number;
  batteryCapacityMwh?: number | null;
  gridConnectionVoltage?: string;
  meteringPointCode?: string;
  scadaAvailable?: boolean;
  scadaVendor?: string;
  communicationProtocol?: string;
  dataRefreshInterval?: string;
  tiltAngleDeg?: number | null;
  azimuthDeg?: number | null;
  installedCapacityDcMw?: number | null;
  installedCapacityAcMw?: number | null;
  panelNominalPowerW?: number | null;
  inverterTotalPowerKw?: number | null;
  dcAcRatio?: number | null;
}

interface Props {
  assetId: string;
  initialData?: TechnicalSpec;
  onSaved: () => void;
}

interface ValidationHint {
  field: string;
  message: string;
}

function toNum(v: string): number | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (Number.isNaN(n)) return null;
  return n;
}

function toNumberOrNull(value: string | null | undefined): number | null {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number | null | undefined, digits = 4): string {
  if (n == null || Number.isNaN(n)) return '';
  return n.toFixed(digits).replace(/\.?0+$/, '');
}

function withinPct(a: number, b: number, pct = 3): boolean {
  if (b === 0) return a === 0;
  return Math.abs((a - b) / b) * 100 <= pct;
}

export default function TabTechnical({ assetId, initialData, onSaved }: Props) {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const [form, setForm] = useState<Record<string, string>>({
    inverterType: '',
    inverterCount: '',
    panelType: '',
    panelCount: '',
    batteryCapacityMwh: '',
    gridConnectionVoltage: '',
    meteringPointCode: '',
    scadaAvailable: 'false',
    scadaVendor: '',
    communicationProtocol: '',
    dataRefreshInterval: '',
    tiltAngleDeg: '',
    azimuthDeg: '',
    installedCapacityDcMw: '',
    installedCapacityAcMw: '',
    panelNominalPowerW: '',
    inverterTotalPowerKw: '',
    dcAcRatio: '',
  });

  const [manualDcAc, setManualDcAc] = useState(false);
  const [hints, setHints] = useState<ValidationHint[]>([]);

  const populateForm = useCallback((spec: TechnicalSpec | undefined) => {
    if (!spec) return;
    setForm({
      inverterType: spec.inverterType ?? '',
      inverterCount: spec.inverterCount != null ? String(spec.inverterCount) : '',
      panelType: spec.panelType ?? '',
      panelCount: spec.panelCount != null ? String(spec.panelCount) : '',
      batteryCapacityMwh: spec.batteryCapacityMwh != null ? String(spec.batteryCapacityMwh) : '',
      gridConnectionVoltage: spec.gridConnectionVoltage ?? '',
      meteringPointCode: spec.meteringPointCode ?? '',
      scadaAvailable: spec.scadaAvailable ? 'true' : 'false',
      scadaVendor: spec.scadaVendor ?? '',
      communicationProtocol: spec.communicationProtocol ?? '',
      dataRefreshInterval: spec.dataRefreshInterval ?? '',
      tiltAngleDeg: spec.tiltAngleDeg != null ? String(spec.tiltAngleDeg) : '',
      azimuthDeg: spec.azimuthDeg != null ? String(spec.azimuthDeg) : '',
      installedCapacityDcMw: spec.installedCapacityDcMw != null ? String(spec.installedCapacityDcMw) : '',
      installedCapacityAcMw: spec.installedCapacityAcMw != null ? String(spec.installedCapacityAcMw) : '',
      panelNominalPowerW: spec.panelNominalPowerW != null ? String(spec.panelNominalPowerW) : '',
      inverterTotalPowerKw: spec.inverterTotalPowerKw != null ? String(spec.inverterTotalPowerKw) : '',
      dcAcRatio: spec.dcAcRatio != null ? String(spec.dcAcRatio) : '',
    });
    if (spec.dcAcRatio != null) {
      setManualDcAc(true);
    }
  }, []);

  useEffect(() => {
    populateForm(initialData);
  }, [initialData, populateForm]);

  // Auto-calculate dcAcRatio when DC or AC changes (only if user hasn't manually set it)
  useEffect(() => {
    if (manualDcAc) return;
    const dc = toNum(form.installedCapacityDcMw);
    const ac = toNum(form.installedCapacityAcMw);
    if (dc != null && ac != null && ac > 0) {
      const ratio = dc / ac;
      setForm((prev) => ({ ...prev, dcAcRatio: String(ratio) }));
    }
  }, [form.installedCapacityDcMw, form.installedCapacityAcMw, manualDcAc]);

  // Validation hints
  useEffect(() => {
    const nextHints: ValidationHint[] = [];

    const invKw = toNum(form.inverterTotalPowerKw);
    const acMw = toNum(form.installedCapacityAcMw);
    if (invKw != null && acMw != null && acMw > 0) {
      const invMw = invKw / 1000;
      if (!withinPct(invMw, acMw, 3)) {
        nextHints.push({
          field: 'inverterTotalPowerKw',
          message: `Inverter total power (${fmt(invMw, 3)} MW) differs from installed AC capacity (${fmt(acMw, 3)} MW) by > 3%`,
        });
      }
    }

    const panels = toNum(form.panelCount);
    const panelW = toNum(form.panelNominalPowerW);
    const dcMw = toNum(form.installedCapacityDcMw);
    if (panels != null && panelW != null && panels > 0 && panelW > 0 && dcMw != null && dcMw > 0) {
      const calculatedDcMw = (panels * panelW) / 1_000_000;
      if (!withinPct(calculatedDcMw, dcMw, 3)) {
        nextHints.push({
          field: 'installedCapacityDcMw',
          message: `Calculated DC capacity from panels (${fmt(calculatedDcMw, 4)} MW) differs from entered DC capacity (${fmt(dcMw, 4)} MW) by > 3%`,
        });
      }
    }

    setHints(nextHints);
  }, [form.inverterTotalPowerKw, form.installedCapacityAcMw, form.panelCount, form.panelNominalPowerW, form.installedCapacityDcMw]);

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'dcAcRatio') {
      setManualDcAc(value.trim() !== '');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {
        scadaAvailable: form.scadaAvailable === 'true',
        inverterType: form.inverterType || null,
        inverterCount: toNumberOrNull(form.inverterCount),
        panelType: form.panelType || null,
        panelCount: toNumberOrNull(form.panelCount),
        batteryCapacityMwh: toNumberOrNull(form.batteryCapacityMwh),
        gridConnectionVoltage: form.gridConnectionVoltage || null,
        meteringPointCode: form.meteringPointCode || null,
        scadaVendor: form.scadaVendor || null,
        communicationProtocol: form.communicationProtocol || null,
        dataRefreshInterval: form.dataRefreshInterval || null,
        tiltAngleDeg: toNumberOrNull(form.tiltAngleDeg),
        azimuthDeg: toNumberOrNull(form.azimuthDeg),
        installedCapacityDcMw: toNumberOrNull(form.installedCapacityDcMw),
        installedCapacityAcMw: toNumberOrNull(form.installedCapacityAcMw),
        panelNominalPowerW: toNumberOrNull(form.panelNominalPowerW),
        inverterTotalPowerKw: toNumberOrNull(form.inverterTotalPowerKw),
        dcAcRatio: toNumberOrNull(form.dcAcRatio),
      };

      console.log('TECH_SPEC_PATCH_PAYLOAD', payload);

      await api.patch(`/assets/${assetId}/technical-spec`, payload);
      addToast('Technical specification saved successfully', 'success');
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to save technical spec');
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const inputClass = 'w-full px-3 py-2 rounded-md text-sm outline-none transition-colors';
  const inputStyle = { background: inputBg, border: `1px solid ${inputBorder}`, color: textMain };
  const sectionBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const sectionBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';

  const existingFields = [
    { key: 'inverterType', label: 'Inverter Type', placeholder: 'e.g. SMA Sunny Tripower' },
    { key: 'inverterCount', label: 'Inverter Count', placeholder: 'e.g. 12', type: 'number' },
    { key: 'panelType', label: 'Panel Type', placeholder: 'e.g. JA Solar JAM72S30' },
    { key: 'panelCount', label: 'Panel Count', placeholder: 'e.g. 4800', type: 'number' },
    { key: 'batteryCapacityMwh', label: 'Battery Capacity', unit: 'MWh', placeholder: 'e.g. 2.5', type: 'number' },
    { key: 'gridConnectionVoltage', label: 'Grid Connection Voltage', placeholder: 'e.g. 110 kV' },
    { key: 'meteringPointCode', label: 'Metering Point Code', placeholder: 'e.g. UA-MP-00123456' },
    { key: 'scadaVendor', label: 'SCADA Vendor', placeholder: 'e.g. Siemens WinCC' },
    { key: 'communicationProtocol', label: 'Communication Protocol', placeholder: 'e.g. Modbus TCP, IEC 61850' },
    { key: 'dataRefreshInterval', label: 'Data Refresh Interval', placeholder: 'e.g. 5min, 15min' },
  ];

  const forecastingFields = [
    { key: 'tiltAngleDeg', label: 'Tilt Angle', unit: '\u00B0', placeholder: '25', type: 'number', min: 0, max: 90, step: 0.1 },
    { key: 'azimuthDeg', label: 'Azimuth', unit: '\u00B0', placeholder: '180', type: 'number', min: 0, max: 360, step: 0.1 },
    { key: 'installedCapacityDcMw', label: 'Installed DC Capacity', unit: 'MW', placeholder: '10.500', type: 'number', min: 0, step: 0.001 },
    { key: 'installedCapacityAcMw', label: 'Installed AC Capacity', unit: 'MW', placeholder: '9.500', type: 'number', min: 0, step: 0.001 },
    { key: 'panelNominalPowerW', label: 'Panel Nominal Power', unit: 'W', placeholder: '550', type: 'number', min: 0, step: 0.1 },
    { key: 'inverterTotalPowerKw', label: 'Inverter Total Power', unit: 'kW', placeholder: '9500', type: 'number', min: 0, step: 0.1 },
    { key: 'dcAcRatio', label: 'DC / AC Ratio', unit: 'ratio', placeholder: '1.1053', type: 'number', min: 0, step: 0.0001 },
  ];

  const getHintFor = (key: string): string | null => {
    const h = hints.find((hint) => hint.field === key);
    return h ? h.message : null;
  };

  const renderField = (
    field: {
      key: string;
      label: string;
      unit?: string;
      placeholder: string;
      type?: string;
      min?: number;
      max?: number;
      step?: number;
    },
    required = false
  ) => (
    <div key={field.key}>
      <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>
        {field.label}
        {field.unit && <span className="text-xs font-normal ml-1.5" style={{ color: textMuted }}>({field.unit})</span>}
        {required && <span className="text-xs ml-1" style={{ color: '#D12229' }}>*</span>}
      </label>
      <input
        type={field.type ?? 'text'}
        min={field.min}
        max={field.max}
        step={field.step}
        value={form[field.key]}
        onChange={(e) => handleChange(field.key, e.target.value)}
        placeholder={field.placeholder}
        className={inputClass}
        style={inputStyle}
      />
      {getHintFor(field.key) && (
        <div className="mt-1.5 flex items-start gap-1.5 text-xs" style={{ color: '#B8860B' }}>
          <i className="ri-alert-line mt-0.5 flex-shrink-0"></i>
          <span>{getHintFor(field.key)}</span>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(209,34,41,0.08)' }}
          >
            <i className="ri-settings-3-line text-sm" style={{ color: '#D12229' }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>
              Technical Specification
            </h3>
            <p className="text-xs" style={{ color: textMuted }}>
              Inverters, panels, SCADA and metering configuration
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
          style={{ background: '#D12229' }}
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i> Save
            </>
          )}
        </button>
      </div>

      {/* API Error */}
      {apiError && (
        <div
          className="mb-4 px-4 py-3 rounded-lg text-sm"
          style={{ background: 'rgba(209,34,41,0.06)', border: '1px solid rgba(209,34,41,0.2)', color: '#D12229' }}
        >
          <i className="ri-error-warning-line mr-2"></i>
          {apiError}
        </div>
      )}

      {/* Existing Technical Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {existingFields.map((f) => renderField(f))}

        <div className="md:col-span-2">
          <label
            className="flex items-center gap-3 cursor-pointer p-3 rounded-lg transition-colors"
            style={{ border: `1px solid ${inputBorder}`, background: isDark ? 'rgba(255,255,255,0.02)' : '#fff' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = isDark ? 'rgba(255,255,255,0.02)' : '#fff'; }}
          >
            <div className="relative">
              <input
                type="checkbox"
                checked={form.scadaAvailable === 'true'}
                onChange={(e) => handleChange('scadaAvailable', e.target.checked ? 'true' : 'false')}
                className="sr-only"
              />
              <div
                className="w-10 h-6 rounded-full transition-colors"
                style={{ background: form.scadaAvailable === 'true' ? '#D12229' : isDark ? 'rgba(255,255,255,0.15)' : '#E5E5E8' }}
              >
                <div
                  className="w-4 h-4 bg-white rounded-full absolute top-1 transition-transform"
                  style={{ left: form.scadaAvailable === 'true' ? '22px' : '4px' }}
                ></div>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium" style={{ color: textMain }}>
                SCADA Available
              </div>
              <div className="text-xs" style={{ color: textMuted }}>
                Enable if this asset has SCADA system connected
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Forecasting Technical Parameters */}
      <div className="mb-5 flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(31,91,143,0.08)' }}
        >
          <i className="ri-sun-line text-sm" style={{ color: '#1F5B8F' }}></i>
        </div>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: textMain }}>
            Forecasting Technical Parameters
          </h3>
          <p className="text-xs" style={{ color: textMuted }}>
            These parameters are required for solar generation forecasting
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {forecastingFields.map((f) => renderField(f, true))}
      </div>

      {/* Phase F 2026-05-15: equipment groups CRUD — declarative, separate from forecast formula */}
      <EquipmentGroupsSection type="panel" assetId={assetId} />
      <EquipmentGroupsSection type="inverter" assetId={assetId} />
    </div>
  );
}